import AudioManager from '@repo/shared/AudioManager'
import {AnimationAction} from 'three'

export interface IActions {
  [EAction.Idle]: AnimationAction | null
  [EAction.Race]: AnimationAction | null
  [EAction.Goal1]: AnimationAction | null
  [EAction.Goal2]: AnimationAction | null
  [EAction.Goal3]: AnimationAction | null
  [EAction.Goal4]: AnimationAction | null
}

export enum EAction {
  Idle = 'Idle',
  Race = 'Race',
  Goal1 = 'Goal1',
  Goal2 = 'Goal2',
  Goal3 = 'Goal3',
  Goal4 = 'Goal4',
}

const BASE_SPEED_MULTIPLIER = 4
const IDLE_SPEED_TRESHOLD = 0.01

const audioManager = new AudioManager()

export function PlayerHandler(passedActions: IActions, team: string) {
  // the following variables and functions are declared within the scope of the createBook function so other book instances or code cannot access these variables

  const actions: IActions = passedActions

  let previousAction: AnimationAction | null = null
  let activeAction: AnimationAction | null = null
  let activeActionName: EAction = EAction.Idle
  let previousX: number = 0

  let didFinish: boolean = false

  let isBlocked: boolean = false

  const init = () => {
    // start playing ski sound, but no volume. It is adjusted in setRaceSpeed
    audioManager.SFX.cross_country_skiing
      ?.fade(audioManager.SFX.cross_country_skiing?.volume(), 0, 50)
      .play()
  }

  const setBlocked = () => {
    isBlocked = true
    setTimeout(() => {
      isBlocked = false
    }, 200)
  }

  const fadeToAction = (name: EAction, duration: number) => {
    if (actions === null) return
    setBlocked()

    previousAction = activeAction
    activeAction = actions[name]

    if (previousAction !== activeAction) {
      previousAction?.fadeOut(duration)
    }

    activeAction?.reset().fadeIn(duration).play()

    activeActionName = name
  }

  const getSpeed = (x: number) => {
    if (!previousX) {
      previousX = x
    }

    const diff = Math.abs(x - previousX)

    previousX = x

    return diff ? Math.round(diff * 10) / 10 : 0
  }

  const setRaceSpeed = (speed: number) => {
    if (!activeAction || didFinish) return

    /**
     * Handle the idle animation / race animation transition
     */
    if (!isBlocked && activeActionName !== EAction.Race && speed > IDLE_SPEED_TRESHOLD) {
      // If not in race and we have speed, use the race animation
      fadeToAction(EAction.Race, 0.8)
    } else if (!isBlocked && activeActionName === EAction.Race && speed <= IDLE_SPEED_TRESHOLD) {
      // Fade to the idle animation if we have no speed
      fadeToAction(EAction.Idle, 2)
    }

    /**
     * Set the speed of the race animation if we have speed
     */
    if (activeActionName === EAction.Race && speed > IDLE_SPEED_TRESHOLD) {
      activeAction.setEffectiveTimeScale(speed * BASE_SPEED_MULTIPLIER)
    }

    // Adjust ski sound volume based on speed
    // multiply with 1.4 to get a more dynamic sound
    const volume = Math.min(speed * 1.4, 1)
    audioManager.SFX.cross_country_skiing?.volume(volume)
  }

  const setFinished = (slot: number, position: number) => {
    if (didFinish) return

    didFinish = true
    if (position === 1) {
      fadeToAction(EAction.Goal1, 1.2)
    } else if (position === 2) {
      fadeToAction(EAction.Goal2, 1.2)
    } else if (position === 3) {
      fadeToAction(EAction.Goal3, 1.2)
    } else {
      fadeToAction(EAction.Goal4, 1.2)
    }
  }

  // only the methods listed as key-value pairs can be accessed by the returned Object
  return {
    init,
    fadeToAction,
    getSpeed,
    setRaceSpeed,
    setFinished,
  }
}
